import React, { useState, useEffect } from "react";
import "./TopRightIcons.scss";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { ReactComponent as HelpIcon } from "../../../assets/icons/bulb.svg";
import { ReactComponent as SaveIcon } from "../../../assets/icons/flopyDisk.svg";
import { ReactComponent as PauseIcon } from "../../../assets/icons/pause.svg";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileDownloadDoneIcon from "@mui/icons-material/FileDownloadDone";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";

import IconButton from "../../UI/IconButton/IconButton";

const TopRightIcons = ({
  activeIcon,
  setActiveIcon,
  handlePause,
  handleSave,
  handleHint,
  onDownload,
  handleRearrange,
  handleCalculator,
  handleStickyNotes,
  handleInstruction,
  badgeContent = 0,
  delayTime = 1000, // Default delay of 5 seconds
  disabled = false,
  isZoomIn, // Default to zoom in icon
  isRearrange = false,
  handleMagnifierRef,
  isShowDownload = false,
  highlightedIcon = false,
  isShowCalculator = false,
  isShowStickyNote = false,
  showMagnifierRef = false,
  isDownloadComplete = false,
}) => {
  const [showBadge, setShowBadge] = useState(false);
  const [disableInstruction, setInstruction] = useState(true);
  // Effect to handle the delayed badge display
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowBadge(true);
      setInstruction(false);
    }, delayTime);

    // Clean up timeout on unmount
    return () => clearTimeout(timer);
  }, [delayTime]);

  const handleIconClick = (iconName, callback) => {
    // If we click an icon, set it as active

    setActiveIcon(iconName);
    if (callback) callback();
  };

  const handleRearrangeClick = () => {
    handleIconClick("refresh", handleRearrange);

    // Reset the active icon after a short delay to allow visual feedback
    setTimeout(() => {
      setActiveIcon(null);
    }, 800);
  };

  useEffect(() => {
    if (activeIcon === "save") {
      setTimeout(() => {
        setActiveIcon(null);
      }, 2000);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIcon]);

  return (
    <div className="top-right-icons">
      {/* HINT / HELP button */}
      <IconButton
        edge="end"
        color="inherit"
        className={`icon ${activeIcon === "help" ? "active" : ""}`}
        imageSrc={HelpIcon}
        onClick={() => handleIconClick("help", handleHint)}
        badgeContent={showBadge ? badgeContent : 0} // Only show badge after delay
        disabled={disabled || disableInstruction}
        title="Hint"
        isActive={highlightedIcon === "hint"}
      />
      {/* Magnifier button */}

      {showMagnifierRef && (
        <IconButton
          edge="end"
          color="inherit"
          className={`icon ${activeIcon === "magnifier" ? "active" : ""}`}
          imageSrc={isZoomIn ? ZoomInIcon : ZoomOutIcon}
          onClick={() => handleIconClick("magnifier", handleMagnifierRef)}
          title={"Magnifier"}
        />
      )}

      <IconButton
        edge="end"
        color="inherit"
        className={`icon ${activeIcon === "instruction" ? "active" : ""}`}
        imageSrc={InfoOutlinedIcon}
        onClick={() => handleIconClick("instruction", handleInstruction)}
        title="Instructions"
      />
      {isRearrange && (
        <IconButton
          edge="end"
          color="inherit"
          className={`icon ${activeIcon === "refresh" ? "active" : ""}`}
          imageSrc={RefreshOutlinedIcon}
          onClick={handleRearrangeClick}
          title="Reset"
        />
      )}

      {/* download packet capture data */}

      {isShowDownload && (
        <IconButton
          edge="end"
          color="inherit"
          className={`icon ${activeIcon === "download" ? "active" : ""}`}
          imageSrc={
            isDownloadComplete ? FileDownloadDoneIcon : FileDownloadIcon
          }
          onClick={async () => {
            setActiveIcon("download");
            try {
              // Expect onDownload to return true on success (or nothing).
              const ok = onDownload ? await onDownload() : true;

              // remove active class regardless (you asked to clear after completion)
              setActiveIcon(null);
              return ok;
            } catch (err) {
              // also clear on error so it doesn't stay "active"
              setActiveIcon(null);
              // optional: you likely toast inside onDownload; no-op here
            }
          }}
          title={
            isDownloadComplete ? "Download Completed" : "Download Packet Data"
          }
          isActive={highlightedIcon === "download"}
        />
      )}

      {/* SAVE button */}
      <IconButton
        edge="end"
        color="inherit"
        className={`icon ${activeIcon === "save" ? "active" : ""}`}
        imageSrc={SaveIcon}
        onClick={() => handleIconClick("save", handleSave)}
        title="Save"
        isActive={highlightedIcon === "save"}
      />
      {/* openStickyNotes */}
      {isShowStickyNote && (
        <IconButton
          edge="end"
          color="inherit"
          className={`icon ${activeIcon === "stickyNotes" ? "active" : ""}`}
          imageSrc={NoteAddOutlinedIcon}
          onClick={() => handleIconClick("stickyNotes", handleStickyNotes)}
          title="Sticky Notes"
        />
      )}
      {/* openCalculator */}
      {isShowCalculator && (
        <IconButton
          edge="end"
          color="inherit"
          className={`icon ${activeIcon === "calculator" ? "active" : ""}`}
          imageSrc={CalculateOutlinedIcon}
          onClick={() => handleIconClick("calculator", handleCalculator)}
          title="Calculator"
        />
      )}
      {/* PAUSE button */}
      <IconButton
        edge="end"
        color="inherit"
        className={`icon ${activeIcon === "pause" ? "active" : ""}`}
        imageSrc={PauseIcon}
        onClick={() => handleIconClick("pause", handlePause)}
        title="Pause"
        isActive={highlightedIcon === "pause"}
      />
    </div>
  );
};

export default TopRightIcons;
